/**
 * Standard Operating Procedures — Supabase-backed CRUD service.
 *
 * Schema: supabase/migrations/00306_sops.sql.
 * Types:  types/sop.ts.
 *
 * Phase 1 surface: list / get / create / update / delete / toggle / record
 * evaluation. No runtime engine yet — evaluations are seeded only by the
 * simulator until the engine ships.
 */

import { supabase } from '@/lib/supabase'
import type {
  Sop,
  SopWrite,
  SopRule,
  SopEvaluation,
  SopFinding,
  NotifyChannel,
  SopScopeKind,
  SopTriggerKind,
  SopSeverity,
} from '@/types/sop'

// Lazy import to avoid a service-layer cycle. The engine cache lives in
// sopEngine.ts and must be punched on every write that changes the active
// SOP set.
async function bustEngineCache(): Promise<void> {
  const mod = await import('@/services/sopEngine')
  mod.invalidateSopCache()
}

// ── Row mappers ──────────────────────────────────────────────────────

function mapSopRow(row: Record<string, unknown>): Sop {
  return {
    id: String(row.id),
    name: String(row.name),
    description: (row.description as string | null) ?? null,
    nlBody: String(row.nl_body),
    compiledRule: (row.compiled_rule as SopRule | null) ?? null,
    compiledAt: (row.compiled_at as string | null) ?? null,
    compiledByModel: (row.compiled_by_model as string | null) ?? null,
    scopeKind: row.scope_kind as SopScopeKind,
    scopeTarget: (row.scope_target as string | null) ?? null,
    triggerKind: row.trigger_kind as SopTriggerKind,
    severity: row.severity as SopSeverity,
    advisory: !!row.advisory,
    notifyChannels: ((row.notify_channels as NotifyChannel[] | null) ?? []),
    enabled: !!row.enabled,
    owner: (row.owner as string | null) ?? null,
    category: (row.category as string | null) ?? null,
    createdAt: String(row.created_at),
    createdBy: (row.created_by as string | null) ?? null,
    updatedAt: String(row.updated_at),
    updatedBy: (row.updated_by as string | null) ?? null,
  }
}

function mapEvalRow(row: Record<string, unknown>): SopEvaluation {
  return {
    id: String(row.id),
    sopId: String(row.sop_id),
    entityType: String(row.entity_type),
    entityId: String(row.entity_id),
    passed: !!row.passed,
    findings: ((row.findings as SopFinding[] | null) ?? []),
    payloadSnapshot: (row.payload_snapshot as Record<string, unknown> | null) ?? null,
    notified: ((row.notified as SopEvaluation['notified'] | null) ?? []),
    ranAt: String(row.ran_at),
    ranBy: (row.ran_by as string | null) ?? null,
    triggeredBy: row.triggered_by as SopTriggerKind,
  }
}

function sopToRow(input: SopWrite, compileMeta?: { compiledAt: string; compiledByModel: string }) {
  return {
    name: input.name,
    description: input.description,
    nl_body: input.nlBody,
    compiled_rule: input.compiledRule,
    compiled_at: compileMeta?.compiledAt ?? null,
    compiled_by_model: compileMeta?.compiledByModel ?? null,
    scope_kind: input.scopeKind,
    scope_target: input.scopeTarget,
    trigger_kind: input.triggerKind,
    severity: input.severity,
    advisory: input.advisory,
    notify_channels: input.notifyChannels,
    enabled: input.enabled,
    owner: input.owner,
    category: input.category,
    updated_by: input.updatedBy,
  }
}

// ── Error helper ─────────────────────────────────────────────────────

export function describeSopError(err: unknown): string {
  if (err instanceof Error) return err.message
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown }
    const parts = [e.message, e.details, e.hint].filter((p) => typeof p === 'string') as string[]
    const combined = parts.join(' · ')
    if (combined) return typeof e.code === 'string' ? `${combined} [${e.code}]` : combined
    try { return JSON.stringify(err) } catch { /* fall through */ }
  }
  return String(err)
}

// ── List / get ───────────────────────────────────────────────────────

export async function listSops(): Promise<Sop[]> {
  const { data, error } = await supabase
    .from('sops')
    .select('*')
    .order('category', { ascending: true, nullsFirst: false })
    .order('name', { ascending: true })
  if (error) throw error
  return (data ?? []).map((r) => mapSopRow(r as Record<string, unknown>))
}

export async function getSop(id: string): Promise<Sop | null> {
  const { data, error } = await supabase.from('sops').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return data ? mapSopRow(data as Record<string, unknown>) : null
}

// ── Create / update / delete ─────────────────────────────────────────

export async function createSop(input: SopWrite): Promise<Sop> {
  const row = {
    ...sopToRow(input),
    created_by: input.createdBy,
  }
  const { data, error } = await supabase.from('sops').insert(row).select('*').single()
  if (error) throw error
  await bustEngineCache()
  return mapSopRow(data as Record<string, unknown>)
}

export async function updateSop(
  id: string,
  input: SopWrite,
  compileMeta?: { compiledAt: string; compiledByModel: string },
): Promise<Sop> {
  const { data, error } = await supabase
    .from('sops')
    .update(sopToRow(input, compileMeta))
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  await bustEngineCache()
  return mapSopRow(data as Record<string, unknown>)
}

/**
 * Write only the compile output. Use after a successful compile so we
 * don't have to round-trip the whole edit form.
 */
export async function saveCompiledRule(
  id: string,
  rule: SopRule,
  compiledByModel: string,
): Promise<Sop> {
  const compiledAt = new Date().toISOString()
  const { data, error } = await supabase
    .from('sops')
    .update({
      compiled_rule: rule,
      compiled_at: compiledAt,
      compiled_by_model: compiledByModel,
    })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  return mapSopRow(data as Record<string, unknown>)
}

export async function toggleSopEnabled(id: string, enabled: boolean): Promise<void> {
  const { error } = await supabase.from('sops').update({ enabled }).eq('id', id)
  if (error) throw error
  await bustEngineCache()
}

export async function deleteSop(id: string): Promise<void> {
  const { error } = await supabase.from('sops').delete().eq('id', id)
  if (error) throw error
  await bustEngineCache()
}

// ── Evaluations ──────────────────────────────────────────────────────

export async function listRecentEvaluations(limit = 50): Promise<SopEvaluation[]> {
  const { data, error } = await supabase
    .from('sop_evaluations')
    .select('*')
    .order('ran_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []).map((r) => mapEvalRow(r as Record<string, unknown>))
}

export async function listEvaluationsForSop(sopId: string, limit = 50): Promise<SopEvaluation[]> {
  const { data, error } = await supabase
    .from('sop_evaluations')
    .select('*')
    .eq('sop_id', sopId)
    .order('ran_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []).map((r) => mapEvalRow(r as Record<string, unknown>))
}

export async function recordEvaluation(args: {
  sopId: string
  entityType: string
  entityId: string
  passed: boolean
  findings: SopFinding[]
  payloadSnapshot?: Record<string, unknown>
  triggeredBy: SopTriggerKind
  ranBy?: string
}): Promise<SopEvaluation> {
  const { data, error } = await supabase
    .from('sop_evaluations')
    .insert({
      sop_id: args.sopId,
      entity_type: args.entityType,
      entity_id: args.entityId,
      passed: args.passed,
      findings: args.findings,
      payload_snapshot: args.payloadSnapshot ?? null,
      triggered_by: args.triggeredBy,
      ran_by: args.ranBy ?? null,
    })
    .select('*')
    .single()
  if (error) throw error
  return mapEvalRow(data as Record<string, unknown>)
}
